import { CurrencyPipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Sku } from 'src/app/common/Sku';
import { ProductFilter } from 'src/app/common/ProductFilter';
import { SkuService } from 'src/app/services/sku.service';
import { faEdit, faTrashAlt, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { Observable, take, tap } from 'rxjs';
import { AppProfileService } from 'src/app/services/profile.service';
import { ProfileRoleConstants } from 'src/app/common/ProfileRole';
import { ExtendedProfile } from 'src/app/common/ExtendedProfile';
import { GridModule, PDFModule } from '@progress/kendo-angular-grid';
import { PagedList } from 'src/app/common/PagedList';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ProductSearchComponent } from '../product-search/product-search.component';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { GridPdfColumnsDirective } from '../grid/grid-pdf-columns.directive';
import { PdfPageComponent } from '../grid/pdf-page.component';
import { GridNoRecordsComponent } from '../grid/grid-no-records.component';

@Component({
    imports: [CurrencyPipe, GridModule, FontAwesomeModule, PDFModule, RouterModule, ProductSearchComponent, AsyncClickDirective, GridPdfColumnsDirective, PdfPageComponent, GridNoRecordsComponent],
    selector: 'app-sku-list',
    templateUrl: './sku-list.component.html',
    styleUrls: ['./sku-list.component.css']
})
export class SkuListComponent implements OnInit {
  productSkuService = inject(SkuService);
  private router = inject(Router);
  private profileService = inject(AppProfileService);
  private confirmActionService = inject(CallApiService);

  public faEdit = faEdit;
  public faTrashAlt = faTrashAlt;
  public faFilePdf = faFilePdf;

  /**
   * #178 / backend #688: the category column's labels. `productCategoryNames` is resolved for the
   * language this app asked for; `productCategoryShortNames` are the stable ASCII keys, which is
   * what the column showed until now — „Buecher", „WerkzeugeMaschinen", in both languages alike.
   * The keys stay as the fallback for a backend that predates the resolved names.
   */
  public categoryNames(sku: Sku): string[] {
    return sku?.productCategoryNames?.length
      ? sku.productCategoryNames
      : (sku?.productCategoryShortNames ?? []);
  }

  public filter: ProductFilter = new ProductFilter(true, false, '', null);
  public isAdmin: boolean = false;
  public isCarrier: boolean = false;
  public profile: ExtendedProfile;

  // #648: server-side paging/sorting; whitelist-backed columns only.
  public list = new PagedList<Sku>({
    sortFields: { name1: 'name1', number: 'number' },
    defaultSort: [{ field: 'name1', dir: 'asc' }],
    fetch: (paging) =>
      this.filter
        ? this.productSkuService.get(
            this.filter.isActive,
            this.filter.isInactive,
            this.filter.name,
            this.filter.categoryId,
            paging
          )
        : null,
  });

  public ngOnInit() {
    this.profileService.currentProfile$.pipe(take(1)).subscribe((profile) => {
      this.isAdmin = profile.role == ProfileRoleConstants.RoleIdAdmin;
      this.profile = profile;
    });
    this.list.load();
  }

  public onEdit(skuId: string) {
    this.router.navigate(['/sku-edit/' + skuId]);
  }

  public search(filter: ProductFilter) {
    this.filter = filter;
    this.list.search();
  }

  // Returns a factory so [asyncClick] can guard this per-row delete against a
  // double-click (the confirm() + request run when the returned fn is invoked).
  // The reload steps back a page if the deletion emptied the last one.
  onDelete(id: number): () => Observable<unknown> {
    return () =>
      this.confirmActionService.confirmAndCallWithToasts$(
        this.productSkuService.delete(id),
        $localize`Sind Sie sicher, dass Sie den Artikel löschen möchten?`,
        $localize`Artikel wurde gelöscht.`,
        $localize`Beim Löschen des Artikels ist ein Fehler aufgetreten.`
      )
      .pipe(tap(() => this.list.load()));
  }
}
